import { useDroppable } from "@z/dnd";
import { allPanes } from "../model/tree";
import type { Edge } from "../model/types";
import { cn } from "./cn";
import { dockCollision } from "./dnd";
import { useLayout } from "./LayoutContext";

const targets: Record<Edge, string> = {
  left: "inset-y-0 left-0 w-[18px]",
  right: "inset-y-0 right-0 w-[18px]",
  top: "inset-x-0 top-0 h-[18px]",
  bottom: "inset-x-0 bottom-0 h-[18px]",
};
const previews: Record<Edge, string> = {
  left: "inset-y-0 left-0 w-1/2",
  right: "inset-y-0 right-0 w-1/2",
  top: "inset-x-0 top-0 h-1/2",
  bottom: "inset-x-0 bottom-0 h-1/2",
};

export function WorkspaceDockTargets() {
  const { draggingPaneId, tree, compact, maximized } = useLayout();
  const disabled = !draggingPaneId || compact || maximized || allPanes(tree).length < 2;
  return (
    <div className="pointer-events-none absolute inset-0 z-40">
      {(["left", "right", "top", "bottom"] as const).map((edge) => (
        <WorkspaceDock key={edge} edge={edge} disabled={disabled} />
      ))}
    </div>
  );
}

function WorkspaceDock({ edge, disabled }: { edge: Edge; disabled: boolean }) {
  const { ref, isDropTarget } = useDroppable({
    id: `workspace:${edge}`,
    accept: "pane",
    disabled,
    collisionDetector: dockCollision,
    data: {
      workspace: true,
      edge,
      keyboardAxis: edge === "left" || edge === "right" ? "horizontal" : "vertical",
      label: `Split workspace ${edge}`,
    },
  });
  return (
    <>
      <div
        ref={ref}
        data-layout-workspace-target={edge}
        className={cn("absolute", targets[edge], !disabled && "bg-accent/10")}
      />
      {!disabled && isDropTarget && (
        <div
          data-layout-workspace-preview={edge}
          className={cn(
            "absolute m-1 flex items-center justify-center rounded-lg border-2 border-accent bg-accent-muted/80 text-sm font-medium text-accent",
            previews[edge],
          )}
        >
          Split workspace {edge}
        </div>
      )}
    </>
  );
}
